import CancelIcon from "@mui/icons-material/Cancel";
import DownloadIcon from "@mui/icons-material/Download";
import FilterListIcon from "@mui/icons-material/FilterList";
import SearchIcon from "@mui/icons-material/Search";
import ViewColumnIcon from "@mui/icons-material/ViewColumn";
import {
  Badge,
  Box,
  Chip,
  InputAdornment,
  styled,
  TextField,
  Tooltip,
} from "@mui/material";
import {
  ColumnsPanelTrigger,
  DataGridPro,
  FilterPanelTrigger,
  GRID_CHECKBOX_SELECTION_COL_DEF,
  GRID_CHECKBOX_SELECTION_FIELD,
  gridRowSelectionCountSelector,
  QuickFilter,
  QuickFilterClear,
  QuickFilterControl,
  QuickFilterTrigger,
  Toolbar,
  ToolbarButton,
  useGridApiContext,
} from "@mui/x-data-grid-pro";
import { SnackbarContext } from "components/context/SnackbarProvider";
import { useCacheDiscardEventListener } from "components/shared/apiCrudBuilder";
import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import DisplayStateButton from "./DisplayStateButton";
import PredefinedFilters from "./PredefinedFilters";

const StyledQuickFilter = styled(QuickFilter)({
  display: "grid",
  alignItems: "center",
});

const StyledToolbarButton = styled(ToolbarButton)(({ theme, ownerState }) => ({
  gridArea: "1 / 1",
  width: "min-content",
  height: "min-content",
  zIndex: 1,
  opacity: ownerState?.expanded ? 0 : 1,
  pointerEvents: ownerState?.expanded ? "none" : "auto",
  transition: theme.transitions.create(["opacity"]),
}));

const StyledTextField = styled(TextField)(({ theme, ownerState }) => ({
  gridArea: "1 / 1",
  overflowX: "clip",
  width: ownerState.expanded ? 260 : "var(--trigger-width)",
  opacity: ownerState.expanded ? 1 : 0,
  transition: theme.transitions.create(["width", "opacity"]),
}));

const getFilteredRows = ({ apiRef }) =>
  gridExpandedSortedRowIdsSelector(apiRef);

const ColumnsButton = () => {
  return (
    <Tooltip title="Columns">
      <ColumnsPanelTrigger render={<ToolbarButton />}>
        <ViewColumnIcon sx={{ ml: "auto" }} fontSize="small" />
      </ColumnsPanelTrigger>
    </Tooltip>
  );
};

const FilterButton = () => {
  return (
    <Tooltip title="Filters">
      <FilterPanelTrigger
        render={(triggerProps, state) => (
          <ToolbarButton {...triggerProps} color="default">
            <Badge
              badgeContent={state.filterCount}
              color="primary"
              variant="dot"
            >
              <FilterListIcon fontSize="small" />
            </Badge>
          </ToolbarButton>
        )}
      />
    </Tooltip>
  );
};

const QuickFilterButton = () => {
  const apiRef = useGridApiContext();

  const state = apiRef.current.state;
  const quickFilterValues = state.filter?.filterModel?.quickFilterValues || [];
  const [expanded, setExpanded] = useState(quickFilterValues.length > 0);

  return (
    <StyledQuickFilter
      expanded={expanded}
      onExpandedChange={setExpanded}
      debounceMs={500}
      onReset={() => setExpanded(false)}
    >
      <QuickFilterTrigger
        render={(triggerProps) => (
          <Tooltip title="Search" enterDelay={0}>
            <StyledToolbarButton
              {...triggerProps}
              ownerState={{ expanded }}
              color="default"
              disabled={expanded}
            >
              <SearchIcon fontSize="small" />
            </StyledToolbarButton>
          </Tooltip>
        )}
      />
      <QuickFilterControl
        render={({ ref, ...controlProps }, state) => (
          <StyledTextField
            {...controlProps}
            ownerState={{ expanded }}
            inputRef={ref}
            aria-label="Search"
            placeholder="Search..."
            size="small"
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
                endAdornment: state.value ? (
                  <InputAdornment position="end">
                    <QuickFilterClear
                      edge="end"
                      size="small"
                      aria-label="Clear search"
                      material={{ sx: { marginRight: -0.75 } }}
                      onClick={() => {
                        setExpanded(false);
                      }}
                    >
                      <CancelIcon fontSize="small" />
                    </QuickFilterClear>
                  </InputAdornment>
                ) : null,
                ...controlProps.slotProps?.input,
              },
              ...controlProps.slotProps,
            }}
          />
        )}
      />
    </StyledQuickFilter>
  );
};

const DownloadButton = () => {
  const apiRef = useGridApiContext();
  const handleExport = (options) => apiRef.current.exportDataAsCsv(options);

  const getDateTimeString = () => {
    const now = new Date();
    const pad = (n) => n.toString().padStart(2, "0");
    return (
      now.getFullYear().toString() +
      pad(now.getMonth() + 1) +
      pad(now.getDate()) +
      "_" +
      pad(now.getHours()) +
      pad(now.getMinutes()) +
      pad(now.getSeconds())
    );
  };

  return (
    <Tooltip title="Download CSV">
      <ToolbarButton
        color="default"
        onClick={() =>
          handleExport({
            getRowsToExport: getFilteredRows,
            fileName: `data_export_${getDateTimeString()}`,
          })
        }
      >
        <DownloadIcon fontSize="small" />
      </ToolbarButton>
    </Tooltip>
  );
};

const CustomToolbar = ({ predefinedFilters }) => {
  const apiRef = useGridApiContext();
  const [rowsCount, setRowsCount] = useState(apiRef.current.getRowsCount());
  const [rowsSelectionCount, setRowsSelectionCount] = useState(
    gridRowSelectionCountSelector(apiRef),
  );

  useEffect(() => {
    const handleRowsChange = () => {
      setRowsCount(apiRef.current.getRowsCount());
    };
    const handleSelectionChange = () => {
      setRowsSelectionCount(gridRowSelectionCountSelector(apiRef));
    };

    const unsubscribeRowsChange = apiRef.current.subscribeEvent(
      "rowsSet",
      handleRowsChange,
    );
    const unsubscribeSelectionChange = apiRef.current.subscribeEvent(
      "rowSelectionChange",
      handleSelectionChange,
    );

    return () => {
      unsubscribeRowsChange();
      unsubscribeSelectionChange();
    };
  }, [apiRef]);

  // Hide the "MUI X Missing license key" warning - waiting for paying for the license
  if (typeof document !== "undefined") {
    const style = document.createElement("style");
    style.innerHTML = `
        div[style*="z-index: 100000"] {
          display: none;
        }
      `;
    document.head.appendChild(style);
  }

  const chipLabel = useMemo(() => {
    const countString = `${rowsCount} row${rowsCount > 1 ? "s" : ""}`;
    let selectedString = "";
    if (rowsSelectionCount > 0) {
      selectedString = ` (${rowsSelectionCount} selected)`;
    }
    return `${countString}${selectedString}`;
  }, [rowsCount, rowsSelectionCount]);

  return (
    <Toolbar>
      <PredefinedFilters predefinedFilters={predefinedFilters} />
      <DisplayStateButton variant="outlined" sx={{ mr: "auto" }}>
        Filter
      </DisplayStateButton>
      <Chip label={chipLabel} />
      <ColumnsButton />
      <FilterButton />
      <DownloadButton />
      <QuickFilterButton />
    </Toolbar>
  );
};

const CustomTable = ({
  apiRef,
  columns,
  initialState: initialStateParent,
  dataSource,
  defaultFilter,
  onSelect,
  displayState: parentDisplayState,
  onDisplayStateChange: changeParentDisplayState,
  predefinedFilters,
}) => {
  const { displayError } = useContext(SnackbarContext);
  const [displayState, setDisplayState] = useState(parentDisplayState);
  const initialState = useMemo(() => {
    return {
      ...initialStateParent,
      pinnedColumns: {
        ...initialStateParent?.pinnedColumns,
        left: [
          GRID_CHECKBOX_SELECTION_FIELD,
          ...(initialStateParent?.pinnedColumns?.left ?? []),
        ],
      },
      ...displayState,
    };
  }, []);

  const dataSourceFiltered = useMemo(() => {
    const newGetRows = ({ filterModel, ...props }) => {
      const filterModelCombined = {
        ...filterModel,
        ...defaultFilter,
        items: [...(filterModel?.items || []), ...(defaultFilter?.items || [])],
      };

      return dataSource.getRows({ filterModel: filterModelCombined, ...props });
    };
    return {
      ...dataSource,
      getRows: newGetRows,
    };
  }, [dataSource, defaultFilter]);

  useCacheDiscardEventListener([dataSource?.key], () => {
    apiRef.current?.dataSource?.cache.clear();
    apiRef.current?.dataSource?.fetchRows();
  });

  useEffect(() => {
    changeParentDisplayState && changeParentDisplayState(displayState);
  }, [displayState]);

  const handleStateChange = (state) => {
    setDisplayState((ds) => {
      if (
        ds.pinnedColumns !== state.pinnedColumns ||
        ds.filter.filterModel !== state.filter.filterModel
      ) {
        return {
          pinnedColumns: state.pinnedColumns,
          filter: {
            filterModel: state.filter.filterModel,
          },
        };
      } else {
        return ds;
      }
    });
  };

  const columnsWithSelect = useMemo(
    () => [
      ...columns,
      {
        ...GRID_CHECKBOX_SELECTION_COL_DEF,
        disableClickEvent: true,
      },
    ],
    [columns],
  );

  const handleSelectionChange = useCallback(
    (
      params, // GridRowParams
    ) => {
      onSelect && onSelect(params);
    },
    [onSelect],
  );

  const handleError = useCallback((params) => {
    displayError(params);
  });

  const refBox = useRef();
  const [appBarHeight, setAppBarHeight] = useState(64);

  useEffect(() => {
    if (typeof window !== "undefined" && refBox.current) {
      const rect = refBox.current.getBoundingClientRect();
      setAppBarHeight(rect.top);
    }
  }, []);

  return (
    <Box ref={refBox} height={`calc(100vh - ${appBarHeight}px)`} width="100%">
      <DataGridPro
        apiRef={apiRef}
        density={"compact"}
        columns={columnsWithSelect}
        dataSource={dataSourceFiltered}
        initialState={initialState}
        onStateChange={handleStateChange}
        showToolbar
        checkboxSelection
        disableRowSelectionOnClick
        onRowSelectionModelChange={handleSelectionChange}
        slots={{
          toolbar: CustomToolbar,
        }}
        slotProps={{
          toolbar: {
            quickFilterProps: { debounceMs: 500 },
            predefinedFilters: predefinedFilters,
          },
        }}
        hideFooter
        onDataSourceError={handleError}
      />
    </Box>
  );
};

export default CustomTable;
